import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  Typography,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetActivityReport } from '~/thunks/report/reportThunk';
import { handleApiError } from '~/utils/helper';
import { IActivityReport } from '~/utils/interface/report';
import { HeadCelType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';

const ActivityReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      { id: 'organization', label: t('properties.organization'), sortable: true, searchable: true },
      { id: 'contact', label: t('properties.contact'), sortable: false, searchable: false },
      { id: 'note', label: t('properties.note'), sortable: false, searchable: false },
    ],
    [t]
  );

  const { selectionAccounts } = useAppSelector((state) => state.account);
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [total, setTotal] = useState<number>();
  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [activity, setActivity] = useState<Array<IActivityReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionAccounts];

  const handleSearch = () => {
    // Dispatch the request to fetch the activity report with the selected dates and account filter
    if (selectedAccount.includes('all')) {
      dispatch(handleGetActivityReport({ startDate, endDate }))
        .unwrap()
        .then((response) => {
          setActivity(response.responses || []);
          setTotal(response.result.count);
        })
        .catch(handleApiError);
    } else {
      dispatch(handleGetActivityReport({ startDate, endDate, createdBy: selectedAccount ?? [] }))
        .unwrap()
        .then((response) => {
          setActivity(response.responses || []);
          setTotal(response.result.count);
        })
        .catch(handleApiError);
    }
  };

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetActivityReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);
        setTotal(response.result.count);
        setSelectedAccount(['all']);
        setActivity(response.responses || []);
      })
      .catch(handleApiError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.activity')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.createdBy')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.createdBy')}
            placeholder={t('properties.createdBy')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[600px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.id}
                  className={`${
                    cell.id === 'note' ? 'w-[60%]' : 'w-[20%]'
                  } bg-[#F0F7FF] text-[#758CA5] text-lg font-bold`}
                >
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {activity.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-[20%]'>{row.organization}</TableCell>
                <TableCell className='w-[20%]'>{row.contact}</TableCell>
                <TableCell className='w-[60%]' dangerouslySetInnerHTML={{ __html: row.note }}></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className='flex justify-between items-center p-2 bg-[#F0F7FF] border-t border-gray-300'>
        <Typography variant='h6' component='div' className='font-bold text-[#758CA5]'>
          {t('total')}
        </Typography>
        <Typography variant='h6' component='div' className='font-bold text-[#758CA5]'>
          {total}
        </Typography>
      </Box>
    </Box>
  );
};

export default ActivityReport;
